import React from 'react';
import _ from 'lodash';

import {
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  keyframes,
  styled
} from '@mui/material';

import { TabBar } from 'iron-forge-react-modules';
import { PermissionName } from 'generated/graphql';
import { permissionUtil } from 'modules/layer00';
import { Loop } from '@mui/icons-material';



const GreenDot = styled('div')({
  width: 20,
  height: 20,
  backgroundColor: '#33F369',
  borderRadius: 10
});

const LabelDiv = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(1),
  whiteSpace: 'nowrap'
}));

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const AnimatedLoop = styled(Loop)({
  animation: `${spin} 2s infinite linear`,
  animationDirection: 'reverse'
});


export interface ITab<T> {
  id: T;
  label: string;
  requiredPermissions: readonly PermissionName[];
}

export interface IProps<TTabId extends string> {
  loading?: boolean;
  value?: TTabId;
  tabId2ItemCount: Record<TTabId, number>;
  onChange: (value: TTabId) => void;
}

export function genTabBarComponent<T extends string>({
  tabs
}: {
  tabs: readonly ITab<T>[];
}) {
  const FC: React.FC<IProps<typeof tabs[number]['id']>> = props => {
    const [hasPermissions, permissionsLoading] = permissionUtil.usePermissions({ evaluationCriteria: 'all' });
    const tabsToShow = React.useMemo(
      () => {
        return _.map(tabs, (tab): TabBar.ITab | null => {
          return hasPermissions(tab.requiredPermissions) ? {
            id: tab.id,
            label: tab.label,
            elIcon: props.loading ? <AnimatedLoop fontSize="small" /> : props.tabId2ItemCount[tab.id] > 0 ? (
              <Chip
                size="small"
                label={props.tabId2ItemCount[tab.id]}
                sx={{
                  color: 'red',
                  textTransform: 'lowercase'
                }} />) : <GreenDot />,
            iconPosition: 'end'
          } : null;
        });
      },
      [
        hasPermissions,
        props.tabId2ItemCount,
        props.loading
      ]
    );

    return (
      <ToggleButtonGroup
        exclusive
        color="primary"
        value={props.value}
        size="small"
        onChange={(ev, value) => {
          if (value) {
            props.onChange(value);
          }
        }}>
        {!permissionsLoading && _.map(_.compact(tabsToShow), tab => (
          <ToggleButton
            key={tab.id}
            value={tab.id}>
            <LabelDiv>
              {tab.label}
            </LabelDiv>

            {tab.elIcon}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  return FC;
}
